import { useFiles } from "../api/files.api.ts";
import { useTestCases } from "../api/test-cases.api.ts";
import { useTickets } from "../api/tickets.api.ts";
import { useHealth } from "../api/health.api.ts";
import { Badge, Card, Page } from "../components/Page.tsx";

export function Dashboard() {
  const tickets = useTickets({ limit: 500 });
  const tests = useTestCases(500);
  const files = useFiles();
  const health = useHealth();

  const ticketsByType = (tickets.data?.tickets ?? []).reduce(
    (acc, t) => {
      const k = t.type ?? "unknown";
      acc[k] = (acc[k] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const recent = (tickets.data?.tickets ?? []).slice(0, 5);

  return (
    <Page
      title="Dashboard"
      description="What's been ingested, and where things stand."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card title="Tickets">
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-semibold">
              {tickets.data?.tickets.length ?? "—"}
            </span>
            <span className="text-sm text-zinc-500">total</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Object.entries(ticketsByType).map(([k, v]) => (
              <Badge key={k} tone="info">
                {k}: {v}
              </Badge>
            ))}
          </div>
        </Card>
        <Card title="Test cases">
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-semibold">
              {tests.data?.testCases.length ?? "—"}
            </span>
            <span className="text-sm text-zinc-500">total</span>
          </div>
        </Card>
        <Card title="Documents">
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-semibold">
              {files.data?.files.length ?? "—"}
            </span>
            <span className="text-sm text-zinc-500">files</span>
          </div>
          <div className="mt-2 text-sm text-zinc-500">
            {(files.data?.files ?? []).reduce(
              (s, f) => s + f.chunkCount,
              0,
            )}{" "}
            chunks
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card title="Recent tickets">
          {recent.length === 0 ? (
            <p className="text-sm text-zinc-500">
              No tickets yet. Upload Jira JSON on the Upload tab.
            </p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {recent.map((t) => (
                <li key={t.id} className="py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-zinc-500">
                      {t.id}
                    </span>
                    {t.type && <Badge tone="info">{t.type}</Badge>}
                    {t.status && <Badge tone="neutral">{t.status}</Badge>}
                  </div>
                  <div className="mt-0.5 text-sm">{t.title}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mt-6 text-xs text-zinc-500">
        Server:{" "}
        {health.data
          ? `${health.data.status} · v${health.data.version} · up ${health.data.uptime.toFixed(0)}s`
          : "checking…"}
      </div>
    </Page>
  );
}
